/* =========================================================
   Row-level access control.

   Every list screen sees only what the active role's scope
   allows:
     ETC Admin    → everything
     Partner Mgr  → their organization(s), all their sites and
                    the instructors assigned to them
     Site Manager → their site(s) and those instructors
   ========================================================= */
import { useMemo } from 'react';
import type { Instructor, Organization, Role, SessionUser, Site } from '@/types';
import { ds, userOrgIds } from '@/data';
import { completeness } from '@/lib/fields';
import { siteSchema } from '@/lib/fields';
import { esDeUnSite } from '@/lib/roles';

export interface ScopedData {
  orgs: Organization[];
  sites: Site[];
  instr: Instructor[];
  disassociated: number;
  sitesWithRed: number;
  /** Weighted progress: pooled required fields, not an average of averages. */
  pct: number;
}

/** True when the site still has an empty red field or a '0000' postal code. */
export function siteHasRed(s: Site): boolean {
  return !s.RegisteredPostalCode || !s.CommercialSiteName || !s.Workstations || s.SitePostalCode === '0000';
}

export function useScopedData(role: Role, user: SessionUser | null): ScopedData {
  return useMemo(() => {
    let orgs = ds.listOrgs();
    let sites = ds.listSites();
    let instr = ds.listInstructors();

    if (role === 'PARTNER_MGR') {
      const oids = userOrgIds(user);
      orgs = orgs.filter((o) => oids.includes(o.OrgId));
      sites = sites.filter((s) => oids.includes(s.OrgId));
      const aIds = ds.listAssignments().filter((a) => oids.includes(a.OrgId)).map((a) => a.InstructorId);
      instr = instr.filter((i) => aIds.includes(i.InstructorId));
    } else if (esDeUnSite(role) && user?.siteId) {
      sites = sites.filter((s) => s.SiteId === user.siteId);
      orgs = orgs.filter((o) => o.OrgId === user.orgId);
      const aIds = ds.listAssignments({ siteId: user.siteId }).map((a) => a.InstructorId);
      instr = instr.filter((i) => aIds.includes(i.InstructorId));
    }

    const disassociated = instr.filter((i) => i.disassociated).length;
    const sitesWithRed = sites.filter(siteHasRed).length;

    // Pooled, not averaged: a site with 20 required fields weighs more
    // than one with 5.
    let totalReq = 0;
    let filled = 0;
    sites.forEach((s) => {
      const c = completeness(siteSchema, s);
      totalReq += c.total;
      filled += c.total - c.missing;
    });
    const pct = totalReq ? Math.round((filled / totalReq) * 100) : 100;

    return { orgs, sites, instr, disassociated, sitesWithRed, pct };
  }, [role, user]);
}
